import { area as d3Area, line as d3Line } from 'd3-shape';

import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_FILL_OPACITY,
  DEFAULT_SERIES_CURVE_SHAPE,
} from '../../../../constants.js';
import { OVERLAY_PADDING } from '../../../data-point/constants.js';
import {
  AreaCanvasInstance,
  Orientation,
  FnCanvasAccessor,
  ORIENTATION_VERTICAL,
} from '../../types/canvas-shape.js';
import {
  ARC_BATCH_SIZE,
  PATH_BATCH_SIZE,
  createBatchedStrokeContext,
} from '../batched-path-context.js';
import { getCanvasColor } from '../canvas-color.js';
import { mapSeriesCurveToD3 } from '../map-series-curve-to-d3.js';

type FnAreaCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  areaCanvas: AreaCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y0: FnCanvasAccessor<Datum>,
  y1: FnCanvasAccessor<Datum>,
  orientation?: Orientation,
) => void;

function drawScatterPoint(
  ctx: CanvasRenderingContext2D,
  scatterX: number,
  scatterY: number,
  scatterRadius: number,
  canvasWidth: number,
  canvasHeight: number,
): boolean {
  const isDataOutOfBoundaries =
    scatterY > canvasHeight ||
    scatterY < 0 ||
    scatterX > canvasWidth ||
    scatterX < 0;

  if (!isDataOutOfBoundaries) {
    ctx.moveTo(scatterX, scatterY);
    ctx.arc(scatterX, scatterY, scatterRadius, 0, 2 * Math.PI);
    return true;
  }

  return false;
}

/**
 * Builds style FN Area based on parameters.
 *
 * @param strokeWidth - stroke width
 * @param radius - scatter plot radius
 * @param lineStrokeWidth - upper line stroke width
 * @param enableDotOverflow - enable dots indicators to be drawn overflowing the plot area
 * @param overlayPadding - padding for overlay
 * @returns - fn print Area canvas
 */
export function printAreaBuilder<Datum>(
  strokeWidth: number,
  radius: number = DEFAULT_DATA_POINT_RADIUS,
  lineStrokeWidth: number,
  enableDotOverflow: boolean,
  overlayPadding: number = OVERLAY_PADDING,
): FnAreaCanvasStyle<Datum> {
  const area = d3Area<Datum>();
  const line = d3Line<Datum>();

  return (ctx, areaCanvas, x, y0, y1, orientation = ORIENTATION_VERTICAL) => {
    const {
      color,
      strokeColor,
      lineColor,
      data,
      opacity = DEFAULT_FILL_OPACITY,
      defined = () => true,
      displayScatterPlot,
      curve = DEFAULT_SERIES_CURVE_SHAPE,
      canvasWidth,
      canvasHeight,
      showBandedArea,
    } = areaCanvas;

    const [firstDatum] = data;
    const computedColor = color(firstDatum);
    const isGradient = typeof computedColor !== 'string';
    const datumColor = !isGradient ? getCanvasColor(computedColor) : undefined;
    const gradientColor = isGradient ? computedColor : undefined;
    const datumStrokeColor =
      strokeColor && getCanvasColor(strokeColor(firstDatum));
    const lineStrokeColor = lineColor && getCanvasColor(lineColor(firstDatum));
    const linesCurve = mapSeriesCurveToD3(curve);
    const hasToPrintScatter = data.length && displayScatterPlot?.(data, 0);

    ctx.save();

    if (enableDotOverflow) {
      ctx.translate(overlayPadding / 2, overlayPadding / 2);
      ctx.save();

      ctx.beginPath();
      ctx.rect(0, 0, canvasWidth, canvasHeight);
      ctx.clip();
    }
    // Shared styles
    ctx.filter = 'none';
    ctx.globalAlpha = opacity;

    // Draw area
    ctx.beginPath();
    ctx.lineJoin = 'bevel';

    // Set stroke styles BEFORE path generation so that batched intermediate
    // flushes render with correct appearance.
    if (datumStrokeColor || datumColor) {
      ctx.strokeStyle = (datumStrokeColor || datumColor) ?? '';
      ctx.lineWidth = strokeWidth;
    }

    const batchedCtx = createBatchedStrokeContext(ctx, PATH_BATCH_SIZE);

    let areaPath;
    const isHorizontal = orientation === 'horizontal';

    if (isHorizontal) {
      areaPath = area
        .defined(defined)
        .curve(linesCurve)
        .y(x)
        .x0(y0)
        .x1(y1)
        .context(batchedCtx);
    } else {
      areaPath = area
        .defined(defined)
        .curve(linesCurve)
        .x(x)
        .y0(y0)
        .y1(y1)
        .context(batchedCtx);
    }

    areaPath(data);

    if (data.length) {
      if (datumStrokeColor || datumColor) {
        // Style area border
        ctx.stroke();
      }
    }

    // Fill area with a color o a gradient
    if (datumColor) {
      ctx.fillStyle = datumColor;
      ctx.fill();
    } else if (gradientColor) {
      gradientColor({ context: ctx });
    }

    if (enableDotOverflow) {
      ctx.restore();
    }

    if (hasToPrintScatter) {
      // Set fill styles before the loop so batched flushes use correct appearance.
      if (datumColor) {
        ctx.fillStyle = datumColor;
      }
      ctx.filter = 'none';
      ctx.globalAlpha = opacity;

      ctx.beginPath();
      const dataLength = data.length;
      const scatterRadius = radius;
      let arcCount = 0;

      for (let i = 0; i < dataLength; i++) {
        if (!defined(data[i])) {
          continue;
        }
        const scatterX = isHorizontal ? y1(data[i], i) : x(data[i], i);
        const scatterY = isHorizontal ? x(data[i], i) : y1(data[i], i);

        if (
          drawScatterPoint(
            ctx,
            scatterX,
            scatterY,
            scatterRadius,
            canvasWidth,
            canvasHeight,
          )
        ) {
          arcCount++;
        }

        if (showBandedArea) {
          const bottomScatterX = isHorizontal ? y0(data[i], i) : x(data[i], i);
          const bottomScatterY = isHorizontal ? x(data[i], i) : y0(data[i], i);

          if (
            drawScatterPoint(
              ctx,
              bottomScatterX,
              bottomScatterY,
              scatterRadius,
              canvasWidth,
              canvasHeight,
            )
          ) {
            arcCount++;
          }
        }

        if (arcCount >= ARC_BATCH_SIZE) {
          ctx.fill();
          ctx.beginPath();
          arcCount = 0;
        }
      }

      ctx.fill();
    }

    // Style sparkline line
    if (lineStrokeColor) {
      ctx.beginPath();

      ctx.strokeStyle = lineStrokeColor;
      ctx.lineWidth = lineStrokeWidth;

      const batchedLineCtx = createBatchedStrokeContext(ctx, PATH_BATCH_SIZE);

      let linePath;
      if (isHorizontal) {
        linePath = line
          .defined(defined)
          .curve(linesCurve)
          .x(y1)
          .y(x)
          .context(batchedLineCtx);
      } else {
        linePath = line
          .defined(defined)
          .curve(linesCurve)
          .x(x)
          .y(y1)
          .context(batchedLineCtx);
      }
      linePath(data);

      ctx.stroke();
    }

    ctx.restore();
  };
}
